import { Box, Link, Text } from '@chakra-ui/react';
import { useEffect, FC, useState } from 'react';
import { useLocation } from 'react-router-dom';

export interface ITermageddonPolicyProps {
  policyId: string;
}

export const TermageddonPolicy: FC<ITermageddonPolicyProps> = ({
  policyId,
}) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    setHtml(null);
    setError(false);

    fetch(
      `https://embed.termageddon.com/api/render/${policyId}?origin=${encodeURIComponent(
        window.location.href,
      )}`,
    )
      .then((res) => res.text())
      .then((data) => {
        if (!cancelled) {
          setHtml(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [policyId, location.pathname]);

  if (error) {
    return (
      <Text>
        There was an error loading this policy.{' '}
        <Link
          isExternal
          href={`https://policies.termageddon.com/api/policy/${policyId}`}
          target="_blank"
          rel="nofollow noreferrer"
          sx={{
            textDecor: 'underline',
          }}
        >
          View it here
        </Link>
        .
      </Text>
    );
  }

  if (!html) {
    return (
      <>
        <Text
          sx={{
            '&::after': {
              content: '""',
              animation: 'dots 1.5s steps(3, end) infinite',
            },
            '@keyframes dots': {
              '0%': { content: '""' },
              '33%': { content: '"."' },
              '66%': { content: '".."' },
              '100%': { content: '"..."' },
            },
          }}
        >
          We're just fetching the latest policy
        </Text>
        <Text>
          If it does not load, you can&nbsp;
          <Link
            isExternal
            href={`https://policies.termageddon.com/api/policy/${policyId}`}
            target="_blank"
            rel="nofollow noreferrer"
            sx={{
              textDecor: 'underline',
            }}
          >
            view it here
          </Link>
        </Text>
      </>
    );
  }

  return (
    <Box
      sx={{
        fontSize: 'md',
        color: 'gray.700',

        p: {
          mb: 4,
        },

        '.tg-last-updated-date': {
          fontSize: 'xs',
          color: 'gray.500',
          mb: 6,
        },

        h3: {
          fontSize: 'xl',
          fontWeight: 'semibold',
          mt: 8,
          mb: 3,
          color: 'gray.800',
        },

        h4: {
          fontSize: 'lg',
          fontWeight: 'semibold',
          mt: 4,
          mb: 1,
          color: 'gray.700',
        },

        ul: {
          pl: 6,
          mb: 4,
        },

        ol: {
          pl: 6,
          mb: 4,
        },

        li: {
          mb: 1,
        },

        a: {
          color: 'blue.600',
          textDecoration: 'underline',
          _hover: { color: 'blue.700' },
        },

        /* Accordion styling */
        '.accordions': {
          my: 6,
        },

        details: {
          border: '1px solid',
          borderColor: 'gray.200',
          borderRadius: 'md',
          mb: 3,
          bg: 'gray.50',
        },

        summary: {
          cursor: 'pointer',
          listStyle: 'none',
          px: 4,
          py: 3,
          fontWeight: 'medium',
          _hover: { bg: 'gray.100' },
        },

        '.accordion-heading': {
          fontSize: 'md',
          fontWeight: 'medium',
          display: 'inline',
        },

        '.accordion-content': {
          px: 4,
          py: 3,
          bg: 'white',
        },

        '.accordion-section': {
          mb: 3,
        },

        '.accordion-section-heading': {
          fontSize: 'xs',
          fontWeight: 'semibold',
          textTransform: 'uppercase',
          color: 'gray.500',
          mb: 1,
        },

        '.accordion-section-content': {
          fontSize: 'sm',
        },
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
};
